import React, { PureComponent, Fragment } from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { Card, CardBody } from "reactstrap";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import LinearLoading from "../../shared/components/LinearLoading";
import PauseIcon from "mdi-react/PauseCircleOutlineIcon";
import PlayIcon from "mdi-react/PlayCircleOutlineIcon";
import EmailIcon from "mdi-react/EmailOutlineIcon";
import DeleteIcon from "mdi-react/TrashCanOutlineIcon";
import OpenIcon from "mdi-react/MagnifyIcon";
import EditIcon from "mdi-react/PencilOutlineIcon";
import ChevronRightIcon from "mdi-react/ChevronRightIcon";
import ChevronDownIcon from "mdi-react/ChevronDownIcon";
import SaveAlertModal from "../ExpiredListings/components/SaveAlertModal";
import { buildFilterChips, buildGeoChips } from "../ExpiredListings/components/alertFilterFormatters";
import moment from "moment";
import { _axiosCall } from "../../shared/helpers/apicalls";

const apiUrl = process.env.API_ROOT;

const TYPE_LABELS = {
	expired: "Expired",
	pom: "Permanently off-market",
	tom: "Temporarily off-market"
};

class MyAlerts extends PureComponent {
	state = {
		loading: true,
		subscriptions: [],
		actioning: {}, // { id: 'pausing' | 'testing' | 'deleting' }
		message: null,
		editingSubscription: null,
		expanded: {},      // { subscriptionId: true } — which rows show their run history
		runsBySub: {},     // { subscriptionId: [runs] }
		runsLoading: {}    // { subscriptionId: true }
	};

	componentDidMount() {
		this._load().then(subs => this._handleUnsubscribeParam(subs));
	}

	// Email "Unsubscribe" links land here as `?unsubscribe=<subId>` (see
	// alerts_controller BuildEmailHtml). We pause the matching alert client-side
	// — the user is already authenticated, so going through the API GET endpoint
	// would only duplicate the auth/CRUD path. Strip the param afterward so a
	// refresh doesn't pause again.
	_handleUnsubscribeParam = async (subs) => {
		const userKey = this._userKey();
		if (!userKey) return;
		const params = new URLSearchParams(this.props.location.search || "");
		const targetId = params.get("unsubscribe");
		if (!targetId) return;

		const sub = (subs || []).find(s => String(s._id) === String(targetId));

		// Always strip the query param so refreshes don't re-fire.
		const search = new URLSearchParams(this.props.location.search || "");
		search.delete("unsubscribe");
		const cleanSearch = search.toString();
		this.props.history.replace(this.props.location.pathname + (cleanSearch ? "?" + cleanSearch : ""));

		if (!sub) {
			this.setState({
				message: { text: "We couldn't find that alert in your account. It may have already been deleted." }
			});
			return;
		}
		if (sub.paused) {
			this.setState({
				message: { text: `"${sub.name}" was already paused — no daily emails will be sent.` }
			});
			return;
		}

		this._setActioning(sub._id, "pausing");
		try {
			await _axiosCall({
				url: `${apiUrl}/expired-listing-alerts/${encodeURIComponent(userKey)}/${sub._id}`,
				method: "put",
				query: { paused: true },
				headers: { "Content-Type": "application/json" }
			});
		} catch (e) {
			this._clearActioning(sub._id);
			this.setState({
				message: { text: `We couldn't pause "${sub.name}". Please try the pause button below.` }
			});
			return;
		}
		this._clearActioning(sub._id);
		this.setState({
			message: { text: `Unsubscribed — "${sub.name}" is now paused. Use the play icon below to resume anytime.` }
		});
		this._load();
	};

	_userKey = () => {
		const { user } = this.props;
		return user && user.id ? user.id : null;
	};

	_load = async () => {
		const userKey = this._userKey();
		if (!userKey) {
			this.setState({ loading: false, message: "Please log in to view your alerts." });
			return [];
		}
		this.setState({ loading: true });
		try {
			const res = await _axiosCall({
				url: `${apiUrl}/expired-listing-alerts/${encodeURIComponent(userKey)}`,
				method: "get"
			});
			const subs = (res && res.subscriptions) || [];
			this.setState({ loading: false, subscriptions: subs });
			return subs;
		} catch (e) {
			this.setState({ loading: false, message: "Could not load alerts." });
			return [];
		}
	};

	_setActioning = (id, kind) => {
		this.setState(s => ({ actioning: Object.assign({}, s.actioning, { [id]: kind }) }));
	};

	_clearActioning = id => {
		this.setState(s => {
			const next = Object.assign({}, s.actioning);
			delete next[id];
			return { actioning: next };
		});
	};

	_toggleExpand = async sub => {
		const userKey = this._userKey();
		if (!userKey) return;
		const id = sub._id;
		const wasOpen = !!this.state.expanded[id];
		this.setState(s => ({ expanded: Object.assign({}, s.expanded, { [id]: !wasOpen }) }));
		// Lazy-load runs the first time we expand (or refresh on every expand for freshness)
		if (!wasOpen) {
			this._loadRunsFor(sub);
		}
	};

	_loadRunsFor = async sub => {
		const userKey = this._userKey();
		if (!userKey) return;
		const id = sub._id;
		this.setState(s => ({ runsLoading: Object.assign({}, s.runsLoading, { [id]: true }) }));
		try {
			const res = await _axiosCall({
				url: `${apiUrl}/expired-listing-alerts/${encodeURIComponent(userKey)}/${id}/runs?limit=10`,
				method: "get"
			});
			const runs = (res && res.runs) || [];
			this.setState(s => ({
				runsBySub: Object.assign({}, s.runsBySub, { [id]: runs }),
				runsLoading: Object.assign({}, s.runsLoading, { [id]: false })
			}));
		} catch (e) {
			this.setState(s => ({
				runsLoading: Object.assign({}, s.runsLoading, { [id]: false })
			}));
		}
	};

	_togglePause = async sub => {
		const userKey = this._userKey();
		if (!userKey) return;
		this._setActioning(sub._id, sub.paused ? "resuming" : "pausing");
		await _axiosCall({
			url: `${apiUrl}/expired-listing-alerts/${encodeURIComponent(userKey)}/${sub._id}`,
			method: "put",
			query: { paused: !sub.paused },
			headers: { "Content-Type": "application/json" }
		});
		this._clearActioning(sub._id);
		this._load();
	};

	_sendTest = async sub => {
		const userKey = this._userKey();
		if (!userKey) return;
		this._setActioning(sub._id, "testing");
		const res = await _axiosCall({
			url: `${apiUrl}/expired-listing-alerts/${encodeURIComponent(userKey)}/${sub._id}/test`,
			method: "post",
			query: {},
			headers: { "Content-Type": "application/json" }
		});
		this._clearActioning(sub._id);
		const run = res && res.run;
		const sent = res && res.result && res.result.sent;
		const found = res && res.stats && res.stats.numFound;
		if (run && run._id) {
			this.setState({
				message: {
					text: sent
						? `Test run complete — ${sent} listings emailed (dummy contacts, $0 cost). View the full run details:`
						: `Test run complete — no new listings in the last 7 days, so nothing was emailed. ${found ? `(${found} total in window, all already sent in past runs.)` : ""}`,
					link: `/expired-listings/runs/${run._id}`,
					linkLabel: "View run"
				}
			});
		} else {
			this.setState({
				message: { text: (res && res.error) || "Test run failed.", link: null }
			});
		}
	};

	_delete = async sub => {
		const userKey = this._userKey();
		if (!userKey) return;
		if (!window.confirm(`Delete alert "${sub.name}"?`)) return;
		this._setActioning(sub._id, "deleting");
		await _axiosCall({
			url: `${apiUrl}/expired-listing-alerts/${encodeURIComponent(userKey)}/${sub._id}`,
			method: "delete"
		});
		this._clearActioning(sub._id);
		this._load();
	};

	_openInSearch = sub => {
		// Restore the saved filters into the Expired Listings page via URL params.
		// URL sync mirrors what setURLFromState produces; values are JSON-encoded.
		const params = new URLSearchParams();
		Object.keys(sub.filters || {}).forEach(k => {
			const v = sub.filters[k];
			if (v === undefined || v === null) return;
			params.set(k, Array.isArray(v) ? v.join(",") : String(v));
		});
		this.props.history.push(`/expired-listings?${params.toString()}`);
	};

	_isAdmin = () => {
		const { user } = this.props;
		return !!(user && user.role && user.role.indexOf && user.role.indexOf("admin") >= 0);
	};

	_renderRow = sub => {
		const action = this.state.actioning[sub._id];
		const isAdmin = this._isAdmin();
		const types = (sub.offMarketTypes || []).map(t => TYPE_LABELS[t] || t).join(", ");
		const last = sub.lastRunAt ? moment(sub.lastRunAt).fromNow() : "never";

		const chipStyle = (accent) => ({
			display: "inline-block",
			background: accent ? "#faf5fe" : "#f3f3f7",
			border: accent ? "1px solid #e8d5f5" : "1px solid #ddd",
			borderRadius: 12,
			padding: "2px 8px",
			margin: "2px 4px 2px 0",
			fontSize: 12,
			color: accent ? "#4a0a64" : "#444"
		});

		// Geography chips (from saved searchTags) render first and in purple so
		// location context stands out from filter constraints.
		const geoChips = buildGeoChips(sub.searchTags).map((c, i) => (
			<span key={"geo-" + i} style={chipStyle(true)}>
				<span style={{ color: "#780f9e", fontWeight: 500 }}>{c.label}:</span>{" "}
				<span style={{ fontWeight: 600 }}>{c.value}</span>
			</span>
		));

		const filterChips = buildFilterChips(sub.filters).map((c, i) => (
			<span key={"filter-" + i} style={chipStyle(false)}>
				<span style={{ color: "#888", fontWeight: 500 }}>{c.label}:</span>{" "}
				<span style={{ fontWeight: 600 }}>{c.value}</span>
			</span>
		));

		const isOpen = !!this.state.expanded[sub._id];
		const runCount = sub.runCount || 0;

		return (
			<Fragment key={sub._id}>
			<tr style={{ borderTop: "1px solid #eee" }}>
				<td style={{ padding: "12px 0 12px 8px", verticalAlign: "top", width: 36 }}>
					<Tooltip title={isOpen ? "Hide run history" : (runCount === 0 ? "No runs yet" : "Show " + runCount + " " + (runCount === 1 ? "run" : "runs"))} arrow>
						<span>
							<IconButton
								size="small"
								onClick={() => this._toggleExpand(sub)}
								disabled={!!action}
								style={{ color: "#780F9E", padding: 4 }}
							>
								{isOpen ? <ChevronDownIcon size={20} /> : <ChevronRightIcon size={20} />}
							</IconButton>
						</span>
					</Tooltip>
				</td>
				<td style={{ padding: "12px 8px", verticalAlign: "top" }}>
					<div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
						<span>{sub.name}</span>
						{runCount > 0 && (
							<span style={{ fontSize: 11, fontWeight: 600, color: "#780F9E", background: "#faf5fe", border: "1px solid #e8d5f5", padding: "1px 8px", borderRadius: 10 }}>
								{runCount} {runCount === 1 ? "run" : "runs"}
							</span>
						)}
					</div>
					<div style={{ color: "#666", fontSize: 13, marginTop: 2 }}>{types}</div>
					{(geoChips.length > 0 || filterChips.length > 0) && (
						<div style={{ marginTop: 6 }}>{geoChips}{filterChips}</div>
					)}
				</td>
				<td style={{ padding: "12px 8px", verticalAlign: "top", textAlign: "center", fontSize: 13 }}>
					Daily<br />
					<span style={{ color: "#888", fontSize: 12 }}>7am ET</span>
				</td>
				<td style={{ padding: "12px 8px", verticalAlign: "top", textAlign: "center", fontSize: 13 }}>
					{last}
					<br />
					<span style={{ color: "#888", fontSize: 12 }}>
						{sub.lastSentCount || 0} sent
					</span>
				</td>
				<td style={{ padding: "12px 8px", verticalAlign: "top", textAlign: "center" }}>
					<span
						style={{
							display: "inline-block",
							padding: "2px 10px",
							borderRadius: 10,
							fontSize: 12,
							background: sub.paused ? "#f5e2e2" : "#e2f5e8",
							color: sub.paused ? "#933" : "#2a6"
						}}
					>
						{sub.paused ? "Paused" : "Active"}
					</span>
				</td>
				<td style={{ padding: "12px 8px", verticalAlign: "top", textAlign: "right", whiteSpace: "nowrap" }}>
					<Tooltip title={sub.paused ? "Resume daily emails" : "Pause daily emails"} arrow>
						<span>
							<IconButton
								onClick={() => this._togglePause(sub)}
								disabled={!!action}
								style={{ color: "#780F9E" }}
							>
								{action === "pausing" || action === "resuming"
									? <CircularProgress size={22} style={{ color: "#780F9E" }} />
									: (sub.paused ? <PlayIcon size={24} /> : <PauseIcon size={24} />)}
							</IconButton>
						</span>
					</Tooltip>
					{isAdmin && (
						<Tooltip title="Admin only — run a test now using dummy contacts (no Premium Credits used). Sends a sample email and creates a viewable run." arrow>
							<span>
								<IconButton
									onClick={() => this._sendTest(sub)}
									disabled={!!action}
									style={{ color: "#780F9E" }}
								>
									{action === "testing"
										? <CircularProgress size={22} style={{ color: "#780F9E" }} />
										: <EmailIcon size={24} />}
								</IconButton>
							</span>
						</Tooltip>
					)}
					<Tooltip title="Edit this alert" arrow>
						<span>
							<IconButton
								onClick={() => this.setState({ editingSubscription: sub })}
								disabled={!!action}
								style={{ color: "#780F9E" }}
							>
								<EditIcon size={22} />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip title="Open these filters in the Search tab" arrow>
						<span>
							<IconButton
								onClick={() => this._openInSearch(sub)}
								disabled={!!action}
								style={{ color: "#780F9E" }}
							>
								<OpenIcon size={22} />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip title="Delete this alert" arrow>
						<span>
							<IconButton
								onClick={() => this._delete(sub)}
								disabled={!!action}
								style={{ color: "#780F9E" }}
							>
								{action === "deleting"
									? <CircularProgress size={22} style={{ color: "#780F9E" }} />
									: <DeleteIcon size={22} />}
							</IconButton>
						</span>
					</Tooltip>
				</td>
			</tr>
			{isOpen && this._renderRunHistoryRow(sub)}
			</Fragment>
		);
	};

	_renderRunHistoryRow = sub => {
		const runs = this.state.runsBySub[sub._id] || [];
		const loading = !!this.state.runsLoading[sub._id];

		const STATUS_BADGE = {
			complete: { label: "Complete", bg: "#e2f5e8", fg: "#1c7e3a" },
			tracing: { label: "Skip-tracing…", bg: "#EEF4FF", fg: "#2A4365" },
			querying: { label: "Querying…", bg: "#EEF4FF", fg: "#2A4365" },
			enriching: { label: "Building email…", bg: "#EEF4FF", fg: "#2A4365" },
			failed: { label: "Failed", bg: "#fff5f5", fg: "#9B2C2C" }
		};
		// Credit-exhausted runs still complete (the email goes out), but the
		// row should call out the partial trace.
		const _badgeFor = (run) => {
			if (run.status === "complete" && run.skipTraceStoppedReason === "out_of_credits") {
				return { label: "Out of credits", bg: "#fff5e6", fg: "#a36100" };
			}
			return STATUS_BADGE[run.status] || { label: run.status, bg: "#eee", fg: "#444" };
		};

		return (
			<tr key={sub._id + "-runs"} style={{ background: "#fcfbff" }}>
				<td colSpan={6} style={{ padding: "0 8px 16px 32px" }}>
					<div style={{ borderLeft: "3px solid #e8d5f5", paddingLeft: 14 }}>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
							<div style={{ fontSize: 12, color: "#780F9E", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em" }}>
								Recent runs
							</div>
							<Button
								size="small"
								onClick={() => this._loadRunsFor(sub)}
								disabled={loading}
								style={{ textTransform: "none", color: "#780F9E", fontSize: 12 }}
							>
								Refresh
							</Button>
						</div>

						{loading && <LinearLoading />}

						{!loading && runs.length === 0 && (
							<div style={{ padding: "8px 0", fontSize: 13, color: "#888" }}>
								No runs yet. Use the email icon to fire a test run, or wait for the next 7am ET tick.
							</div>
						)}

						{!loading && runs.length > 0 && (
							<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
								<thead>
									<tr style={{ color: "#888", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", textAlign: "left" }}>
										<th style={{ padding: "6px 10px", fontWeight: 500 }}>Date</th>
										<th style={{ padding: "6px 10px", fontWeight: 500, textAlign: "center" }}>Emailed</th>
										<th style={{ padding: "6px 10px", fontWeight: 500, textAlign: "center" }}>Contacts</th>
										<th style={{ padding: "6px 10px", fontWeight: 500, textAlign: "center" }}>Credits</th>
										<th style={{ padding: "6px 10px", fontWeight: 500 }}>Status</th>
										<th style={{ padding: "6px 10px", fontWeight: 500, textAlign: "right" }}></th>
									</tr>
								</thead>
								<tbody>
									{runs.map(run => {
										const badge = _badgeFor(run);
										// Count one per matched owner so the column lines up
										// with credits charged on multi-owner deeds. Falls back
										// to "any phones/emails" for legacy runs.
										const contacts = (run.properties || []).reduce((sum, p) => {
											const owners = p.contact && Array.isArray(p.contact.owners) ? p.contact.owners : null;
											if (owners) return sum + owners.length;
											const legacyHit = p.contact && (
												(p.contact.phones && p.contact.phones.length) ||
												(p.contact.emails && p.contact.emails.length)
											);
											return sum + (legacyHit ? 1 : 0);
										}, 0);
										// "Emailed" = listings actually sent. Used to also show
										// "of N" where N=matchCount, but that included properties
										// already sent in past runs and confused users. Now just
										// the actionable number; full breakdown goes in tooltip.
										const emailed = (run.properties && run.properties.length) || run.sentCount || 0;
										const attempted = run.attemptedCount || 0;
										const totalInWindow = run.matchCount || 0;
										const emailedLabel = String(emailed);
										const tipBits = [];
										if (attempted > 0) tipBits.push(`${attempted} skip-traced`);
										if (totalInWindow > 0) tipBits.push(`${totalInWindow} matched in window`);
										const emailedTip = tipBits.length > 0 ? tipBits.join(" · ") : "Listings emailed in this run";
										return (
											<tr key={run._id} style={{ borderTop: "1px solid #efeaf6" }}>
												<td style={{ padding: "8px 10px" }}>
													<div>{moment(run.runAt).format("MMM D, YYYY · h:mm A")}</div>
													{run.testMode && (
														<span style={{ display: "inline-block", marginTop: 2, fontSize: 10, fontWeight: 700, color: "#7c4a0f", background: "#fff7e6", padding: "1px 6px", borderRadius: 8 }}>TEST</span>
													)}
												</td>
												<td style={{ padding: "8px 10px", textAlign: "center" }}>
													<Tooltip title={emailedTip} arrow>
														<span>{emailedLabel}</span>
													</Tooltip>
												</td>
												<td style={{ padding: "8px 10px", textAlign: "center" }}>
													{run.skipTraceEnabled ? contacts : "—"}
												</td>
												<td style={{ padding: "8px 10px", textAlign: "center" }}>
													{run.skipTraceEnabled ? (run.creditsUsed || 0) : "—"}
												</td>
												<td style={{ padding: "8px 10px" }}>
													<span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600, background: badge.bg, color: badge.fg }}>
														{badge.label}
													</span>
												</td>
												<td style={{ padding: "8px 10px", textAlign: "right" }}>
													<Button
														size="small"
														onClick={() => this.props.history.push("/expired-listings/runs/" + run._id)}
														style={{ textTransform: "none", color: "#780F9E", fontSize: 12 }}
													>
														View →
													</Button>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						)}
					</div>
				</td>
			</tr>
		);
	};

	render() {
		const { loading, subscriptions, message, editingSubscription } = this.state;

		return (
			<Fragment>
			<Card>
						<CardBody>
								{loading && <LinearLoading />}

								{message && (
									<div
										style={{
											background: "#faf5fe",
											border: "1px solid #e8d5f5",
											borderLeft: "3px solid #780F9E",
											padding: "12px 14px",
											borderRadius: 6,
											marginBottom: 12,
											fontSize: 13,
											color: "#4a0a64",
											display: "flex",
											alignItems: "center",
											gap: 12
										}}
									>
										<div style={{ flex: 1 }}>
											{typeof message === "string" ? message : message.text}
											{message && message.link && (
												<Button
													size="small"
													onClick={() => this.props.history.push(message.link)}
													style={{ marginLeft: 8, textTransform: "none", color: "#780F9E", fontWeight: 600 }}
												>
													{message.linkLabel || "View"} →
												</Button>
											)}
										</div>
										<Button
											size="small"
											onClick={() => this.setState({ message: null })}
											style={{ textTransform: "none" }}
										>
											Dismiss
										</Button>
									</div>
								)}

								{!loading && subscriptions.length === 0 && (
									<div style={{ padding: "32px 16px", textAlign: "center" }}>
										<p style={{ color: "#666" }}>You don't have any alerts yet.</p>
										<Button
											variant="contained"
											color="primary"
											onClick={() =>
												this.props.history.push("/expired-listings")
											}
										>
											Browse Expired Listings
										</Button>
									</div>
								)}

								{!loading && subscriptions.length > 0 && (
									<table style={{ width: "100%", borderCollapse: "collapse" }}>
										<thead>
											<tr style={{ borderBottom: "2px solid #eee", textAlign: "left" }}>
												<th style={{ width: 36 }}></th>
												<th style={{ padding: "10px 8px", fontSize: 12, color: "#888", textTransform: "uppercase" }}>
													Alert
												</th>
												<th style={{ padding: "10px 8px", fontSize: 12, color: "#888", textTransform: "uppercase", textAlign: "center" }}>
													Frequency
												</th>
												<th style={{ padding: "10px 8px", fontSize: 12, color: "#888", textTransform: "uppercase", textAlign: "center" }}>
													Last sent
												</th>
												<th style={{ padding: "10px 8px", fontSize: 12, color: "#888", textTransform: "uppercase", textAlign: "center" }}>
													Status
												</th>
												<th style={{ padding: "10px 8px", fontSize: 12, color: "#888", textTransform: "uppercase", textAlign: "right" }}>
													Actions
												</th>
											</tr>
										</thead>
										<tbody>{subscriptions.map(this._renderRow)}</tbody>
									</table>
								)}
					</CardBody>
			</Card>

			<SaveAlertModal
				open={!!editingSubscription}
				onClose={() => this.setState({ editingSubscription: null })}
				subscription={editingSubscription}
				user={this.props.user}
				onSaved={() => {
					this.setState({ editingSubscription: null });
					this._load();
				}}
			/>
			</Fragment>
		);
	}
}

export default withRouter(connect(state => ({ user: state.user }))(MyAlerts));
